import { ModalType } from '@/Enums/Whitelabels/ModalType.enum';
import { IControlOption } from '@/Interfaces/ControlOption.interface';
import { IParsedWhitelabel, IWhitelabel } from '@/Interfaces/Whitelabels/Whitelabel.interface';
import { IWhitelabelFormData } from '@/Interfaces/Whitelabels/WhitelabelFormData.interface';
import { Grid } from '@/components/Grid';
import Pagination from '@/components/Grid/Pagination';
import { EXPAND_OPTIONS } from '@/constants/grid';
import { getWhitelabelRequestPayload } from '@/helpers/Whitelabels/whitelabelForm';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { updateCurrentWhitelabel } from '@/store/slices/currentWhitelabelSlice';
import { resetSelectedWhitelabel, setFilters, setSelectedWhitelabel } from '@/store/slices/whitelabelsSlice';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import {
  type GridCellParams,
  type GridColDef,
  type GridFilterModel,
  GridLogicOperator,
  type GridPaginationModel,
  type GridSortModel,
} from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { createWhitelabel, updateWhitelabel } from '../API';
import WhitelabelModal from '../WhitelabelModal';
import ToolBar from './ToolBar';
import { getColumns } from './columns';

interface IProps {
  rows: IParsedWhitelabel[];
  orgs: IControlOption[];
  loading?: boolean;
  fetchWhitelabels: () => void;
  rowCount?: number;
}

const DataGrid = ({ rows = [], orgs = [], loading, fetchWhitelabels, rowCount = 0 }: IProps) => {
  const dispatch = useAppDispatch();
  const { sort: sortModel, ...filters } = useAppSelector(state => state.whitelabels.filters);
  const paginationModel = useMemo(() => ({ page: filters.page - 1, pageSize: filters.limit }), [filters]);

  const [modalLoading, setModalLoading] = useState<boolean>(false);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
    quickFilterLogicOperator: GridLogicOperator.Or,
    logicOperator: GridLogicOperator.Or,
    quickFilterValues: [],
  });

  const onFilterModelChange = useCallback((newFilterModel: GridFilterModel) => setFilterModel(newFilterModel), []);

  const setQuickFilter = useCallback(
    (quickFilterValues: string[]) => dispatch(setFilters({ search: (quickFilterValues || []).join(' ') })),
    [dispatch],
  );

  const setOrgFilter = useCallback((ids: number[]) => dispatch(setFilters({ organizationIds: ids })), [dispatch]);

  const _setSelectedWhitelabel = useCallback(
    (row: IParsedWhitelabel | IWhitelabelFormData) => {
      dispatch(setSelectedWhitelabel(row));
    },
    [dispatch],
  );
  const _resetSelectedWhitelabel = useCallback(() => {
    dispatch(resetSelectedWhitelabel());
  }, [dispatch]);

  const updateUserWhitelabel = useCallback(
    (updatedWhitelabel: IWhitelabel) => {
      dispatch(updateCurrentWhitelabel(updatedWhitelabel));
    },
    [dispatch],
  );
  //#region modals
  const [modalType, setModalType] = useState<ModalType>(ModalType.New);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const onCreateNewClick = () => {
    setModalOpen(true);
    setModalType(ModalType.New);
  };

  const onViewClick = useCallback(
    (row: IParsedWhitelabel) => {
      setModalType(ModalType.View);
      setModalOpen(true);
      _setSelectedWhitelabel(row);
    },
    [_setSelectedWhitelabel],
  );

  const onModalClose = useCallback(() => {
    setModalOpen(false);
    _resetSelectedWhitelabel();
  }, [_resetSelectedWhitelabel]);

  const onNewWhitelabelSubmit = useCallback(
    async (whitelabel: Partial<IWhitelabelFormData>) => {
      setModalLoading(true);
      try {
        await createWhitelabel(getWhitelabelRequestPayload(whitelabel, true));
        toast.success('Whitelabel created successfully');
        setModalLoading(false);
        fetchWhitelabels();
      } catch (error) {
        setModalLoading(false);
        toast.error('Failed to create whitelabel.');
        throw error;
      }
    },
    [fetchWhitelabels],
  );

  const onViewWhitelabelSubmit = useCallback(
    async (whitelabel: Partial<IWhitelabelFormData>) => {
      setModalLoading(true);
      try {
        const updatedWhitelabel = await updateWhitelabel(getWhitelabelRequestPayload(whitelabel), whitelabel.id!);
        toast.success('Whitelabel updated successfully');
        setModalLoading(false);
        updateUserWhitelabel(updatedWhitelabel);
        fetchWhitelabels();
      } catch (error) {
        setModalLoading(false);
        toast.error('Failed to update whitelabel.');
        throw error;
      }
    },
    [fetchWhitelabels, updateUserWhitelabel],
  );

  const onModalSubmit = useCallback(
    async (whitelabel: Partial<IWhitelabelFormData>) => {
      if (modalType === ModalType.New) {
        return await onNewWhitelabelSubmit(whitelabel);
      }
      return await onViewWhitelabelSubmit(whitelabel);
    },
    [modalType, onNewWhitelabelSubmit, onViewWhitelabelSubmit],
  );
  //#endregion

  //#region columns
  const COLUMNS = useMemo(() => getColumns(onViewClick), [onViewClick]) as GridColDef[];
  //#endregion

  return (
    <>
      <Grid
        sx={{
          '& .cell-mainLogo': {
            padding: 0,
          },
        }}
        loading={loading}
        autosizeOnMount
        autosizeOptions={EXPAND_OPTIONS}
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        showCellVerticalBorder
        showColumnVerticalBorder
        rows={rows}
        columns={COLUMNS}
        columnHeaderHeight={30}
        sortModel={sortModel ?? []}
        onSortModelChange={(newSortModel: GridSortModel) => {
          dispatch(setFilters({ sort: newSortModel?.length ? newSortModel : null }));
        }}
        paginationModel={paginationModel}
        onPaginationModelChange={(newModel: GridPaginationModel) =>
          dispatch(setFilters({ page: newModel.page + 1, limit: newModel.pageSize }))
        }
        initialState={{
          pagination: { paginationModel: { pageSize: 15 } },
        }}
        getCellClassName={(params: GridCellParams<IParsedWhitelabel>) => `cell-${params.field}`}
        pagination
        rowCount={rowCount}
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        slots={{
          pagination: Pagination,
          toolbar: ToolBar,
          columnSortedAscendingIcon: KeyboardArrowUp,
          columnSortedDescendingIcon: KeyboardArrowDown,
        }}
        slotProps={{
          pagination: {
            rowCount,
            loading,
          },
          footer: {
            className: 'relative',
          },
          toolbar: {
            setQuickFilter,
            setOrgFilter,
            onCreateNewClick,
            orgs,
            loading,
          },
        }}
      />
      <WhitelabelModal
        onClose={onModalClose}
        open={modalOpen}
        modalType={modalType}
        onSubmit={onModalSubmit}
        loading={modalLoading}
        setLoading={setModalLoading}
        setSelectedWhitelabel={_setSelectedWhitelabel}
        fetchWhitelabels={fetchWhitelabels}
      />
    </>
  );
};

export default DataGrid;
